import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay
} from '@chakra-ui/react';
import { ImHammer2 } from 'react-icons/im';
import { FiCheck, FiExternalLink, FiPaperclip } from 'react-icons/fi';
import { IoCloseCircle } from 'react-icons/io5';

import { formatCompactDateTimeString, formatDateTimeString } from 'common/helpers';
import { Button } from 'common/materials';
import { TaskActivity, TaskStatusList } from 'data/link/schemas';
import { TicketListItem } from 'models/link/Link';
import { ReplyEmailSupport } from 'models/user/UserSupport';

import ParticipantMessage from './ParticipantMessage';

type Props = {
    isOpen: boolean;
    task?: TicketListItem;
    taskActivity?: TaskActivity;
    currentLinkId: string;
    onClose: () => void;
    onReplyEmail: (reply: ReplyEmailSupport) => void;
    onSubmitComment?: (input: CommentReplyInput) => Promise<boolean>;
    onClickCloseTask: () => void;
    onClickCancelTask: () => void;
    onUpdateProgress: (code: string) => Promise<boolean>;
};

export type CommentReplyInput = {
    message: string;
    objectLink: string;
};

type ReadOnlyFieldProps = {
    label: string;
    value?: React.ReactNode;
    className?: string;
    accent?: boolean;
    emptyText?: string;
};

function MutedPlaceholder({ children }: { children: React.ReactNode }) {
    return <span className="italic text-neutral-400">{children}</span>;
}

function ReadOnlyField({
    label,
    value,
    className = '',
    accent = false,
    emptyText = '(ไม่มีการกำหนด)'
}: ReadOnlyFieldProps) {
    const hasValue =
        value !== undefined && value !== null && (typeof value !== 'string' || value.trim() !== '');

    return (
        <div
            className={`grid gap-1 border-b border-[#F4F5F7] py-2.5 sm:grid-cols-[185px_minmax(0,1fr)] sm:gap-4 ${className}`}
        >
            <p className="pt-px text-sm text-[#5A6070]">{label}</p>
            <div
                className={`min-w-0 whitespace-pre-wrap break-words text-sm leading-6 text-[#1C1E24] ${
                    accent ? 'font-medium text-primary-900' : ''
                }`}
            >
                {hasValue ? value : <MutedPlaceholder>{emptyText}</MutedPlaceholder>}
            </div>
        </div>
    );
}

function getSafeAttachmentUrl(value?: string | null) {
    if (!value) return undefined;

    try {
        const url = new URL(value);
        return url.protocol === 'https:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function formatAttachmentSize(value?: string | null) {
    const bytes = Number.parseFloat(value ?? '');
    if (!Number.isFinite(bytes) || bytes < 0) return undefined;
    if (bytes < 1024) return `${Math.round(bytes)} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getConversationDate(value?: string | null) {
    const formattedDateTime = formatCompactDateTimeString(value);
    if (!formattedDateTime) return '';

    return formattedDateTime.split(/\s+/)[0] || '';
}

function ProgressField({
    value,
    description,
    action
}: {
    value?: string | null;
    description?: string;
    action?: React.ReactNode;
}) {
    const parsedValue = Number.parseFloat(value ?? '');
    const hasProgress = Number.isFinite(parsedValue);
    const percentage = hasProgress ? Math.min(100, Math.max(0, parsedValue)) : 0;

    return (
        <div>
            <p className="mb-2 text-sm font-medium text-[#5A6070]">ความคืบหน้า</p>
            <div className="h-2 w-full overflow-hidden rounded bg-[#E5E7EB]">
                <div
                    className="h-full rounded bg-[#22C55E] transition-[width] duration-300"
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <div className="mt-1.5 flex min-h-[24px] flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <div className="flex min-w-0 items-center gap-1.5 font-medium text-[#16803C]">
                    <FiCheck className="shrink-0" />
                    <span className="shrink-0">{hasProgress ? `${percentage}%` : '-'}</span>
                    {description && <span className="truncate">{description}</span>}
                </div>
                {action && <div className="ml-auto shrink-0">{action}</div>}
            </div>
        </div>
    );
}

type ProgressEditorProps = {
    currentCode?: string | null;
    description?: string;
    disabled?: boolean;
    options: TaskStatusList[];
    value?: string | null;
    onSave: (code: string) => Promise<boolean>;
};

function ProgressEditor(props: ProgressEditorProps) {
    const { currentCode, description, disabled, options, value, onSave } = props;
    const validOptions = useMemo(
        () =>
            options
                .filter(option => Boolean(option.status_code))
                .slice()
                .sort((left, right) => {
                    const leftPercentage = Number.parseFloat(left.percentage ?? '');
                    const rightPercentage = Number.parseFloat(right.percentage ?? '');
                    const leftSortValue = Number.isFinite(leftPercentage)
                        ? leftPercentage
                        : Number.POSITIVE_INFINITY;
                    const rightSortValue = Number.isFinite(rightPercentage)
                        ? rightPercentage
                        : Number.POSITIVE_INFINITY;

                    return leftSortValue - rightSortValue;
                }),
        [options]
    );
    const currentOption = validOptions.find(
        option => option.status_code?.trim() === currentCode?.trim()
    );
    const defaultCode = currentOption?.status_code || validOptions[0]?.status_code || '';
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedCode, setSelectedCode] = useState(defaultCode);

    useEffect(() => {
        setSelectedCode(defaultCode);
        setIsEditing(false);
    }, [defaultCode]);

    const selectedOption = validOptions.find(option => option.status_code === selectedCode);
    const handleSave = async () => {
        if (!selectedCode || selectedCode === currentOption?.status_code) return;

        setIsSaving(true);
        try {
            if (await onSave(selectedCode)) setIsEditing(false);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div>
            <ProgressField
                value={isEditing ? selectedOption?.percentage || value : value}
                description={isEditing ? selectedOption?.description || description : description}
                action={
                    !isEditing && !disabled && validOptions.length > 0 ? (
                        <button
                            type="button"
                            onClick={() => {
                                setSelectedCode(defaultCode);
                                setIsEditing(true);
                            }}
                            className="text-xs font-normal text-[#9099A8] hover:text-primary-900 hover:underline"
                        >
                            แก้ไขความคืบหน้า
                        </button>
                    ) : undefined
                }
            />

            {!disabled && validOptions.length > 0 && isEditing && (
                <div className="mt-2 flex items-center justify-end gap-2">
                    <select
                        value={selectedCode}
                        disabled={isSaving}
                        onChange={event => setSelectedCode(event.target.value)}
                        className="min-w-0 flex-1 rounded-md border border-[#C9CDD4] bg-white px-3 py-2 text-sm text-[#1C1E24]"
                    >
                        {validOptions.map(option => (
                            <option
                                key={option.status_code!}
                                value={option.status_code!}
                            >
                                {option.description || option.status_code}
                                {option.percentage
                                    ? ` — ${option.percentage.replace(/%$/, '')}%`
                                    : ''}
                            </option>
                        ))}
                    </select>
                    <button
                        type="button"
                        disabled={
                            isSaving || !selectedCode || selectedCode === currentOption?.status_code
                        }
                        onClick={handleSave}
                        className="rounded-md bg-primary-900 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
                    </button>
                    <button
                        type="button"
                        disabled={isSaving}
                        onClick={() => {
                            setSelectedCode(defaultCode);
                            setIsEditing(false);
                        }}
                        className="rounded-md border border-[#C9CDD4] px-3 py-2 text-sm text-[#5A6070] disabled:opacity-50"
                    >
                        ยกเลิก
                    </button>
                </div>
            )}
        </div>
    );
}

function TicketDetailModal(props: Props) {
    const {
        isOpen,
        task,
        taskActivity,
        onClose,
        onReplyEmail,
        onSubmitComment,
        onClickCloseTask,
        onClickCancelTask,
        onUpdateProgress
    } = props;
    const [commentMessage, setCommentMessage] = useState('');
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const [isParticipantsExpanded, setIsParticipantsExpanded] = useState(false);
    const [hasParticipantOverflow, setHasParticipantOverflow] = useState(false);
    const chatListRef = useRef<HTMLDivElement>(null);
    const participantListRef = useRef<HTMLParagraphElement>(null);

    const activities = useMemo(
        () => [...(taskActivity?.activity_participant_message ?? [])],
        [taskActivity]
    );
    const conversationActivities = useMemo(
        () =>
            activities.filter(activity => {
                return Boolean(
                    activity.data ||
                        activity.messageText ||
                        activity.remarkEventDetail ||
                        activity.eventMessage ||
                        activity.files?.length
                );
            }),
        [activities]
    );
    const latestConversationKey = conversationActivities[conversationActivities.length - 1]?.key;
    const participantNames = useMemo(
        () =>
            (taskActivity?.activity_participant ?? []).map(
                participant => participant.full_name || participant.link_id || '-'
            ),
        [taskActivity?.activity_participant]
    );
    const participantNamesText = participantNames.join(', ');

    useEffect(() => {
        setCommentMessage('');
        setIsSubmittingComment(false);
        setIsParticipantsExpanded(false);
    }, [isOpen, task?.aobjectlink]);

    useEffect(() => {
        if (!isOpen || isParticipantsExpanded) return;

        const updateOverflow = () => {
            const participantList = participantListRef.current;
            if (!participantList) return;

            setHasParticipantOverflow(
                participantList.scrollHeight > participantList.clientHeight + 1
            );
        };
        const frameId = window.requestAnimationFrame(updateOverflow);
        window.addEventListener('resize', updateOverflow);

        return () => {
            window.cancelAnimationFrame(frameId);
            window.removeEventListener('resize', updateOverflow);
        };
    }, [isOpen, isParticipantsExpanded, participantNamesText]);

    useEffect(() => {
        if (!isOpen || !conversationActivities.length) return;

        const frameId = window.requestAnimationFrame(() => {
            const chatList = chatListRef.current;
            if (chatList) chatList.scrollTop = chatList.scrollHeight;
        });

        return () => window.cancelAnimationFrame(frameId);
    }, [isOpen, task?.aobjectlink, latestConversationKey, conversationActivities.length]);

    const handleSubmitComment = async () => {
        const message = commentMessage.trim();
        if (!task || !message || !onSubmitComment || isSubmittingComment) return;

        setIsSubmittingComment(true);
        try {
            const isSuccess = await onSubmitComment({
                message,
                objectLink: task.aobjectlink
            });

            if (isSuccess) {
                setCommentMessage('');
            }
        } finally {
            setIsSubmittingComment(false);
        }
    };

    if (!task || !taskActivity) return null;

    const attachments = taskActivity.attach_file_list ?? [];
    const activityStatusCode = taskActivity.status?.trim() ?? '';
    const inboxProgress = Number.parseFloat(task.taskStatusPercent);
    const taskStatusOptions = taskActivity.task_status_list ?? [];
    const currentStatusItem =
        taskStatusOptions.find(status => status.status_code?.trim() === activityStatusCode) ||
        (Number.isFinite(inboxProgress)
            ? taskStatusOptions.find(
                  status => Number.parseFloat(status.percentage ?? '') === inboxProgress
              )
            : undefined);
    const currentStatusCode = currentStatusItem?.status_code?.trim() || activityStatusCode;
    const currentStatus = task.activityOverviewStatus || currentStatusItem?.description || '';
    const currentProgress = currentStatusItem?.percentage || task.taskStatusPercent || '0';
    const currentProgressDescription = currentStatusItem?.description || task.taskStatusDesc;
    const normalizedTaskStatus = `${task.activityStatus} ${currentStatus}`.toLowerCase();
    const canUpdateTask =
        !normalizedTaskStatus.includes('checkedout') &&
        !normalizedTaskStatus.includes('finished') &&
        !normalizedTaskStatus.includes('canceled') &&
        !normalizedTaskStatus.includes('cancelled');
    const ticketNumber = taskActivity.ticket?.ticket_no || task.ticketNO || '';
    const createdDate =
        taskActivity.checkin_date || taskActivity.checkin_time
            ? formatDateTimeString(
                  taskActivity.checkin_date ?? undefined,
                  taskActivity.checkin_time ?? undefined
              )
            : '';
    const createdByName =
        taskActivity.created_by_name?.trim() || taskActivity.created_by?.trim() || '';
    const priorityCode = taskActivity.priority_code?.trim();
    const priorityDescription = priorityCode
        ? taskActivity.priority_list?.find(
              priority => priority.priority_code?.trim() === priorityCode
          )?.description
        : undefined;
    const problemTypeCode =
        taskActivity.problem_type_code?.trim() || taskActivity.problem_code?.trim();
    const problemTypeDescription = problemTypeCode
        ? taskActivity.problem_type_list?.find(
              problemType => problemType.problem_code?.trim() === problemTypeCode
          )?.description
        : undefined;
    const hasteDescription =
        (taskActivity.haste
            ? taskActivity.haste_list?.find(
                  haste => haste.haste_code?.trim() === taskActivity.haste?.trim()
              )?.description
            : undefined) ||
        task.haste ||
        taskActivity.haste;
    return (
        <Modal
            size="6xl"
            isOpen={isOpen}
            onClose={onClose}
        >
            <ModalOverlay />
            <ModalContent className="ticket-detail-v2 m-4 h-[calc(100vh-32px)] max-h-[calc(100vh-32px)] w-[calc(100vw-32px)] max-w-[calc(100vw-32px)] overflow-hidden rounded-[6px]">
                <ModalHeader className="min-h-[72px] shrink-0 border-b border-[#E1E4E8] bg-white px-3 py-1 pr-14">
                    {canUpdateTask && (
                        <div className="flex h-full items-stretch gap-1">
                            <button
                                type="button"
                                onClick={onClickCloseTask}
                                className="flex min-w-[76px] flex-col items-center justify-center gap-1 rounded-[6px] px-5 py-2 text-sm font-medium text-[#2563EB] transition-colors hover:bg-[#F8F9FA]"
                            >
                                <ImHammer2 size={24} />
                                <span>ปิดงาน</span>
                            </button>
                            <button
                                type="button"
                                onClick={onClickCancelTask}
                                className="flex min-w-[76px] flex-col items-center justify-center gap-1 rounded-[6px] px-5 py-2 text-sm font-medium text-[#DC2626] transition-colors hover:bg-[#F8F9FA]"
                            >
                                <IoCloseCircle size={25} />
                                <span>ยกเลิกงาน</span>
                            </button>
                        </div>
                    )}
                </ModalHeader>
                <ModalCloseButton className="right-4 top-5" />

                <ModalBody className="min-h-0 flex-1 overflow-y-auto p-0 lg:overflow-hidden">
                    <div className="grid min-h-full bg-white lg:h-full lg:grid-cols-[minmax(0,52%)_minmax(0,48%)]">
                        <div className="min-w-0 bg-white lg:min-h-0 lg:overflow-y-auto lg:border-r lg:border-[#E1E4E8]">
                            <section className="border-b border-[#E1E4E8] px-5 py-4">
                                <p className="mb-2 text-sm font-semibold text-[#1C1E24]">
                                    ข้อมูลงาน
                                </p>

                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                    <span className="rounded-[4px] border border-[#BFDBFE] bg-[#EFF4FF] px-3 py-1 text-xs font-semibold text-[#1D4ED8]">
                                        Work Group:{' '}
                                        {taskActivity.prj_desc || (
                                            <MutedPlaceholder>(ไม่มีการกำหนด)</MutedPlaceholder>
                                        )}
                                    </span>
                                </div>

                                <p className="mb-1 mt-2 text-sm font-medium text-[#5A6070]">
                                    หัวข้องาน
                                </p>
                                <p className="break-words text-base font-semibold leading-6 text-[#1C1E24]">
                                    {taskActivity.subject || task.jobdescription || (
                                        <MutedPlaceholder>(ไม่มีการกำหนด)</MutedPlaceholder>
                                    )}
                                </p>

                                <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#5A6070]">
                                    <span>
                                        วันที่สร้าง{' '}
                                        <span className="font-medium text-[#1C1E24]">
                                            {createdDate || (
                                                <MutedPlaceholder>(ไม่มีการกำหนด)</MutedPlaceholder>
                                            )}
                                        </span>
                                    </span>
                                    <span>
                                        เจ้าของงาน{' '}
                                        <span className="font-medium text-[#1C1E24]">
                                            {createdByName || (
                                                <MutedPlaceholder>(ไม่มีการกำหนด)</MutedPlaceholder>
                                            )}
                                        </span>
                                    </span>
                                </div>
                            </section>

                            <section className="border-b border-[#E1E4E8] px-5 py-2.5">
                                <ProgressEditor
                                    currentCode={currentStatusCode}
                                    value={currentProgress}
                                    description={currentProgressDescription}
                                    options={taskStatusOptions}
                                    disabled={!canUpdateTask}
                                    onSave={onUpdateProgress}
                                />
                            </section>

                            <div className="px-5 py-2">
                                <section>
                                    <p className="border-b border-[#F4F5F7] pb-2 pt-2 text-sm font-semibold text-[#1C1E24]">
                                        มอบหมายผู้รับผิดชอบ
                                    </p>
                                    <div className="grid gap-1 border-b border-[#F4F5F7] py-2.5 sm:grid-cols-[185px_minmax(0,1fr)] sm:gap-4">
                                        <p className="pt-px text-sm text-[#5A6070]">
                                            ผู้รับมอบหมาย
                                        </p>
                                        <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-sm leading-6">
                                            <span className="min-w-0 flex-1 break-words text-[#1C1E24]">
                                                {taskActivity.first_assign_name ||
                                                    task.leaderEN ||
                                                    task.leader || (
                                                        <MutedPlaceholder>
                                                            (ไม่มีการกำหนด)
                                                        </MutedPlaceholder>
                                                    )}
                                            </span>
                                            <span className="ml-auto shrink-0 font-medium text-[#1C1E24]">
                                                Ticket{' '}
                                                <span className="text-[#2563EB]">
                                                    {ticketNumber || (
                                                        <MutedPlaceholder>
                                                            (ไม่มีการกำหนด)
                                                        </MutedPlaceholder>
                                                    )}
                                                </span>
                                            </span>
                                        </div>
                                    </div>

                                    <div className="grid gap-1 border-b border-[#F4F5F7] py-2.5 sm:grid-cols-[185px_minmax(0,1fr)] sm:gap-4">
                                        <p className="pt-px text-sm text-[#5A6070]">
                                            ผู้เกี่ยวข้อง
                                        </p>
                                        <div className="min-w-0">
                                            {participantNamesText ? (
                                                <React.Fragment>
                                                    <p
                                                        ref={participantListRef}
                                                        className={`break-words text-sm leading-6 text-[#1C1E24] ${
                                                            isParticipantsExpanded
                                                                ? ''
                                                                : 'line-clamp-2'
                                                        }`}
                                                    >
                                                        {participantNamesText}
                                                    </p>
                                                    {hasParticipantOverflow && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setIsParticipantsExpanded(
                                                                    value => !value
                                                                )
                                                            }
                                                            className="mt-1 text-xs font-medium text-[#2563EB] hover:underline"
                                                        >
                                                            {isParticipantsExpanded
                                                                ? 'แสดงน้อยลง'
                                                                : 'ดูเพิ่มเติม'}
                                                        </button>
                                                    )}
                                                </React.Fragment>
                                            ) : (
                                                <span className="text-sm text-[#5A6070]">
                                                    ไม่มีผู้เกี่ยวข้อง
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <ReadOnlyField
                                        label="รายละเอียดงาน"
                                        value={taskActivity.detail || task.ticketDetail}
                                        emptyText="(ไม่ได้ระบุรายละเอียด)"
                                    />
                                </section>

                                <section>
                                    <p className="border-b border-[#F4F5F7] pb-2 pt-4 text-sm font-semibold text-[#1C1E24]">
                                        กำหนดการและสถานะ
                                    </p>
                                    <div className="grid md:grid-cols-2 md:gap-6">
                                        <ReadOnlyField
                                            label="ระดับความสำคัญ"
                                            value={priorityDescription}
                                        />
                                        <ReadOnlyField
                                            label="ความเร่งด่วน"
                                            value={hasteDescription}
                                        />
                                    </div>
                                    <div className="grid md:grid-cols-2 md:gap-6">
                                        <ReadOnlyField
                                            label="วันและเวลาเริ่ม"
                                            value={
                                                taskActivity.start_date || taskActivity.start_time
                                                    ? formatDateTimeString(
                                                          taskActivity.start_date ?? undefined,
                                                          taskActivity.start_time ?? undefined
                                                      )
                                                    : undefined
                                            }
                                        />
                                        <ReadOnlyField
                                            label="วันและเวลาสิ้นสุด"
                                            value={
                                                taskActivity.end_date || taskActivity.end_time
                                                    ? formatDateTimeString(
                                                          taskActivity.end_date ?? undefined,
                                                          taskActivity.end_time ?? undefined
                                                      )
                                                    : undefined
                                            }
                                        />
                                    </div>
                                    <ReadOnlyField
                                        label="ประเภทของงาน"
                                        value={problemTypeDescription}
                                    />
                                    <div className="grid gap-1 border-b border-[#F4F5F7] py-2.5 sm:grid-cols-[185px_minmax(0,1fr)] sm:gap-4">
                                        <p className="pt-px text-sm text-[#5A6070]">ไฟล์แนบ</p>
                                        {attachments.length > 0 ? (
                                            <div className="grid min-w-0 grid-cols-1 gap-2 md:grid-cols-2">
                                                {attachments.map((file, index) => {
                                                    const fileUrl = getSafeAttachmentUrl(
                                                        file.file_url
                                                    );
                                                    const fileName =
                                                        file.logical_name ||
                                                        file.file_name ||
                                                        file.description ||
                                                        `ไฟล์แนบ ${index + 1}`;
                                                    const fileSize = formatAttachmentSize(
                                                        file.file_size
                                                    );
                                                    const content = (
                                                        <React.Fragment>
                                                            <FiPaperclip className="shrink-0 text-primary-900" />
                                                            <span
                                                                title={fileName}
                                                                className="min-w-0 flex-1 truncate"
                                                            >
                                                                {fileName}
                                                            </span>
                                                            {fileSize && (
                                                                <span className="shrink-0 text-xs text-neutral-400">
                                                                    {fileSize}
                                                                </span>
                                                            )}
                                                            {fileUrl && (
                                                                <FiExternalLink className="shrink-0" />
                                                            )}
                                                        </React.Fragment>
                                                    );

                                                    return fileUrl ? (
                                                        <a
                                                            key={`${
                                                                file.key_object_link ||
                                                                taskActivity.key
                                                            }-${file.item_no || index}`}
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="flex items-center gap-2 rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA] px-3 py-2 text-sm text-[#5A6070] transition hover:border-[#2563EB] hover:text-[#1D4ED8]"
                                                        >
                                                            {content}
                                                        </a>
                                                    ) : (
                                                        <div
                                                            key={`${
                                                                file.key_object_link ||
                                                                taskActivity.key
                                                            }-${file.item_no || index}`}
                                                            className="flex items-center gap-2 rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA] px-3 py-2 text-sm text-[#5A6070]"
                                                        >
                                                            {content}
                                                            <span className="shrink-0 text-xs text-neutral-400">
                                                                ไม่มีลิงก์
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <MutedPlaceholder>ไม่มีไฟล์แนบ</MutedPlaceholder>
                                        )}
                                    </div>
                                    <div className="grid md:grid-cols-2 md:gap-6">
                                        <ReadOnlyField
                                            label="ระยะเวลาดำเนินการ"
                                            value={
                                                taskActivity.time_duration || taskActivity.use_time
                                            }
                                        />
                                        <ReadOnlyField
                                            label="ความล่าช้า"
                                            value={taskActivity.delay}
                                        />
                                    </div>
                                </section>
                            </div>
                        </div>

                        <section className="flex min-h-[620px] min-w-0 flex-col border-t border-[#E1E4E8] bg-[#F4F5F7] lg:min-h-0 lg:border-l-0 lg:border-t-0">
                            <div className="shrink-0 border-b border-[#E1E4E8] bg-white px-4 py-3">
                                <p className="text-base font-semibold text-[#1C1E24]">
                                    แชทเรื่องงาน
                                </p>
                            </div>

                            <div
                                ref={chatListRef}
                                className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-4 py-3 [scrollbar-gutter:stable]"
                            >
                                {conversationActivities.length ? (
                                    conversationActivities.map((activity, index) => {
                                        const activityDate = getConversationDate(
                                            activity.createdOn
                                        );
                                        const previousActivityDate =
                                            index > 0
                                                ? getConversationDate(
                                                      conversationActivities[index - 1].createdOn
                                                  )
                                                : '';
                                        const showDateSeparator =
                                            Boolean(activityDate) &&
                                            activityDate !== previousActivityDate;

                                        return (
                                            <React.Fragment key={activity.key || index}>
                                                {showDateSeparator && (
                                                    <div className="flex items-center gap-3 py-2 text-xs text-[#9099A8]">
                                                        <span className="h-px flex-1 bg-[#E1E4E8]" />
                                                        <span>{activityDate}</span>
                                                        <span className="h-px flex-1 bg-[#E1E4E8]" />
                                                    </div>
                                                )}
                                                <ParticipantMessage
                                                    task={task}
                                                    activity={activity}
                                                    onReplyEmail={onReplyEmail}
                                                />
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <div className="rounded-[6px] border border-[#E1E4E8] bg-white px-3 py-4 text-sm text-[#5A6070]">
                                        ยังไม่มีข้อความ
                                    </div>
                                )}
                            </div>

                            <div className="shrink-0 border-t border-[#E1E4E8] bg-white p-3">
                                <textarea
                                    value={commentMessage}
                                    maxLength={3000}
                                    rows={3}
                                    placeholder="เขียนข้อความ..."
                                    onChange={event => setCommentMessage(event.target.value)}
                                    className="h-[60px] w-full resize-none rounded-[6px] border border-[#E1E4E8] bg-[#F8F9FA] px-3 py-2 text-sm leading-6 text-[#1C1E24] outline-none transition placeholder:text-[#B0B8C4] focus:border-[#2563EB] focus:bg-white focus:ring-1 focus:ring-[#2563EB]"
                                />
                                <div className="mt-2 flex items-center justify-between gap-3">
                                    <span className="text-xs text-[#9099A8]">
                                        {commentMessage.length}/3000
                                    </span>
                                    <div className="flex items-center gap-3">
                                        {!onSubmitComment && (
                                            <span className="text-xs text-neutral-400">
                                                รอเชื่อมต่อ API
                                            </span>
                                        )}
                                        <Button
                                            disabled={
                                                !onSubmitComment ||
                                                !commentMessage.trim() ||
                                                isSubmittingComment
                                            }
                                            onClick={() => void handleSubmitComment()}
                                            className="rounded-[6px] bg-[#2563EB] text-white transition hover:bg-[#1D4ED8] disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {isSubmittingComment
                                                ? 'กำลังเพิ่ม...'
                                                : 'เพิ่มความคิดเห็น'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
}

export default memo(TicketDetailModal);
